import { useState } from "react";
import { ImageOffIcon, XIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { media } from "@/lib/media";
import { cn } from "@/lib/utils";
import { MediaLinkType } from "@/types";

export const MediaThumbnail = ({
  url,
  type,
  onDelete,
}: {
  url: string;
  type: MediaLinkType;
  onDelete: () => void;
}) => {
  /* State */
  const [hovered, setHovered] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /* Derived */
  const isVideo = type === MediaLinkType.VIDEO_URL;
  const thumbnailSrc = isVideo ? media.createYouTubeThumbnailLink(url) : url;
  const label = isVideo ? "video" : "photo";

  /* Render */
  return (
    <>
      <div
        className="relative inline-block"
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
      >
        {/* Thumbnail */}
        <div
          className={cn(
            "h-24 w-24 cursor-pointer overflow-hidden rounded border border-gray-200 bg-gray-50 transition-transform",
            hovered && "scale-105",
          )}
          onClick={() => window.open(url, "_blank")}
        >
          <MediaThumbnailContent isVideo={isVideo} src={thumbnailSrc} />
        </div>

        {/* Delete button */}
        {hovered && (
          <button
            className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmOpen(true);
            }}
            type="button"
          >
            <XIcon className="h-3 w-3" />
          </button>
        )}
      </div>

      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description={`Are you sure you want to remove this ${label}?`}
        onConfirm={() => {
          mixpanelUtils.trackButtonClick(
            "Delete media",
            getScreenFromPathname(location.pathname),
          );
          setConfirmOpen(false);
          onDelete();
        }}
        onOpenChange={setConfirmOpen}
        open={confirmOpen}
        title={`Delete ${isVideo ? "Video" : "Photo"}`}
      />
    </>
  );
};

const MediaThumbnailContent = ({
  isVideo,
  src,
}: {
  isVideo: boolean;
  src: string | null;
}) => {
  if (!src) {
    return (
      <Column className="h-full w-full items-center justify-center gap-1 bg-gray-100">
        <ImageOffIcon className="h-5 w-5 text-gray-400" />
        <Text className="text-[10px]" variant="muted">
          Failed to load
        </Text>
      </Column>
    );
  }

  return (
    <div className="relative h-full w-full">
      <img
        alt={isVideo ? "Video thumbnail" : "Photo"}
        className="h-full w-full object-cover"
        src={src}
      />
      {/* Play button overlay (videos only) */}
      {isVideo && (
        <Row className="absolute inset-0 items-center justify-center bg-black/20">
          <Row className="h-8 w-8 items-center justify-center rounded-full bg-red-600">
            <div className="ml-0.5 h-0 w-0 border-b-4 border-l-[6px] border-t-4 border-b-transparent border-l-white border-t-transparent" />
          </Row>
        </Row>
      )}
    </div>
  );
};
